import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
  Autocomplete,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { parseISO } from 'date-fns';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import DecimalField from '../shared/DecimalField';
import VehicleRegistrationField, { EMPTY_VEHICLE } from '../vehicles/VehicleRegistrationField';
import { plateFont } from '../../utils/fonts';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import NewCustomerForm, { EMPTY_NEW_CUSTOMER, isNewCustomerValid } from '../shared/NewCustomerForm';
import { customerLabel } from '../../utils/customer';

const TIRE_SET_TYPES = ['summer', 'winter', 'all_season'];
const RIM_TYPES = ['steel', 'alloy', 'tires_only'];
// Where the set is. "Uusittava" is a separate flag, set from the card detail view.
const STATUSES = ['stored', 'on_vehicle', 'disposed'];

const emptyForm = {
  customer_id: null,
  vehicle_id: null,
  storage_location_id: null,
  tire_set_type: 'winter',
  studded: false,
  rim_type: 'steel',
  tire_brand: '',
  tire_model: '',
  tire_size: '',
  // Blank for a set with one size all round; tire_size is then the front size
  tire_size_rear: '',
  dot_code: '',
  tread_depth_fl: '',
  tread_depth_fr: '',
  tread_depth_rl: '',
  tread_depth_rr: '',
  status: 'stored',
  storage_start_date: null,
  last_swap_date: null,
  contract_price: '',
  insurance_included: false,
  service_notes: '',
  condition_notes: '',
  notes: '',
};

const emptyNewVehicle = { ...EMPTY_VEHICLE };

/**
 * Create or edit a hotel card.
 *
 * `prefill` (create mode only) starts the form from what the caller already knows:
 * `{ customer, vehicle, tire_set_type, status, lockVehicle }`. With `lockVehicle` the vehicle
 * cannot be changed and the storage section is hidden: the tire swap dialog uses this for the
 * set coming off the car, which the swap itself then puts into storage.
 *
 * `onSaved` receives the saved card.
 */
function TireHotelCardForm({ t, open, onClose, card, onSaved, prefill }) {
  const { currentOrganization } = useUserStore();
  const [form, setForm] = useState({ ...emptyForm });
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [hasRearSize, setHasRearSize] = useState(false);

  // Customer mode: existing vs new
  const [customerMode, setCustomerMode] = useState('existing');
  const [newCustomer, setNewCustomer] = useState({ ...EMPTY_NEW_CUSTOMER });

  // Vehicle: one registration field that either resolves to an existing vehicle
  // or carries the details typed for a new one (same flow as work order creation)
  const [newVehicle, setNewVehicle] = useState({ ...emptyNewVehicle });

  // Customer autocomplete state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const customerSearchTimeoutRef = useRef(null);

  // Vehicle autocomplete state
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehicleSearchInput, setVehicleSearchInput] = useState('');
  const vehicleSearchTimeoutRef = useRef(null);

  // Location autocomplete state
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const locationTimeoutRef = useRef(null);

  // Customer search with debounce
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      try {
        const response = await axios.get('/api/customers/?page_size=50');
        const customersData = response.data.results || response.data;
        setCustomers(Array.isArray(customersData) ? customersData : []);
      } catch (err) {
        console.error('Error loading customers:', err);
      }
      return;
    }

    setCustomerSearchLoading(true);
    try {
      const response = await axios.get(`/api/customers/?search=${encodeURIComponent(query)}&typeahead=true`);
      const customersData = response.data.results || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (err) {
      console.error('Error searching customers:', err);
    } finally {
      setCustomerSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (customerMode !== 'existing') return;
    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }
    customerSearchTimeoutRef.current = setTimeout(() => {
      searchCustomers(customerSearchInput);
    }, 300);
    return () => {
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }
    };
  }, [customerSearchInput, searchCustomers, customerMode]);

  // Fetch vehicles for the edit-mode picker (create mode uses the registration field)
  useEffect(() => {
    if (!card?.id) {
      setVehicles([]);
      return;
    }
    if (vehicleSearchTimeoutRef.current) {
      clearTimeout(vehicleSearchTimeoutRef.current);
    }
    vehicleSearchTimeoutRef.current = setTimeout(async () => {
      setVehiclesLoading(true);
      try {
        const searchParam = vehicleSearchInput && vehicleSearchInput.length >= 1
          ? `&search=${encodeURIComponent(vehicleSearchInput)}`
          : '';
        const response = await axios.get(`/api/vehicles/?page_size=50${searchParam}`);
        const vehiclesData = response.data.results || response.data;
        setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
      } catch (err) {
        console.error('Error fetching vehicles:', err);
      } finally {
        setVehiclesLoading(false);
      }
    }, 300);
    return () => {
      if (vehicleSearchTimeoutRef.current) {
        clearTimeout(vehicleSearchTimeoutRef.current);
      }
    };
  }, [card?.id, vehicleSearchInput]);

  // Suggest locations when the tire size or the typed search changes. The search has to go to
  // the server: it returns a capped slice, so filtering client-side would hide every location
  // that did not make the cut. A staggered set sends both sizes; the wider one decides.
  const rearSizeForSuggest = hasRearSize ? form.tire_size_rear : '';
  useEffect(() => {
    if (locationTimeoutRef.current) {
      clearTimeout(locationTimeoutRef.current);
    }
    locationTimeoutRef.current = setTimeout(async () => {
      if (!currentOrganization?.organization_id) return;
      setLocationsLoading(true);
      try {
        const query = new URLSearchParams();
        if (form.tire_size) query.set('tire_size', form.tire_size);
        if (rearSizeForSuggest) query.set('tire_size_rear', rearSizeForSuggest);
        if (locationSearch) query.set('search', locationSearch);
        const params = query.toString() ? `?${query.toString()}` : '';
        const response = await axios.get(`/api/storage-locations/suggest/${params}`, {
          headers: { 'X-Organization-Id': currentOrganization.organization_id },
        });
        setLocations(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error fetching locations:', err);
      } finally {
        setLocationsLoading(false);
      }
    }, 300);
    return () => {
      if (locationTimeoutRef.current) {
        clearTimeout(locationTimeoutRef.current);
      }
    };
  }, [form.tire_size, rearSizeForSuggest, locationSearch, currentOrganization?.organization_id]);

  // Populate form for edit mode
  useEffect(() => {
    if (!open) return;

    if (card) {
      // Edit mode — always use existing customer/vehicle
      setCustomerMode('existing');
      setNewVehicle({ ...emptyNewVehicle });
      setForm({
        customer_id: card.customer,
        vehicle_id: card.vehicle,
        storage_location_id: card.storage_location,
        tire_set_type: card.tire_set_type || 'winter',
        studded: card.studded || false,
        rim_type: card.rim_type || 'steel',
        tire_brand: card.tire_brand || '',
        tire_model: card.tire_model || '',
        tire_size: card.tire_size || '',
        tire_size_rear: card.tire_size_rear || '',
        dot_code: card.dot_code || '',
        tread_depth_fl: card.tread_depth_fl != null ? String(card.tread_depth_fl) : '',
        tread_depth_fr: card.tread_depth_fr != null ? String(card.tread_depth_fr) : '',
        tread_depth_rl: card.tread_depth_rl != null ? String(card.tread_depth_rl) : '',
        tread_depth_rr: card.tread_depth_rr != null ? String(card.tread_depth_rr) : '',
        status: card.status || 'stored',
        storage_start_date: card.storage_start_date ? parseISO(card.storage_start_date) : null,
        last_swap_date: card.last_swap_date ? parseISO(card.last_swap_date) : null,
        contract_price: card.contract_price != null ? String(card.contract_price) : '',
        insurance_included: card.insurance_included || false,
        service_notes: card.service_notes || '',
        condition_notes: card.condition_notes || '',
        notes: card.notes || '',
      });
      setHasRearSize(!!card.tire_size_rear);

      const fetchRelated = async () => {
        if (card.customer) {
          try {
            const response = await axios.get(`/api/customers/${card.customer}/`);
            setSelectedCustomer(response.data);
          } catch (err) {
            console.error('Error fetching customer:', err);
          }
        }
        if (card.vehicle) {
          try {
            const response = await axios.get(`/api/vehicles/${card.vehicle}/`);
            setSelectedVehicle(response.data);
          } catch (err) {
            console.error('Error fetching vehicle:', err);
          }
        }
        if (card.storage_location && card.location_label) {
          setSelectedLocation({ id: card.storage_location, label: card.location_label });
        }
      };
      fetchRelated();
    } else {
      // Create mode — reset everything, then apply what the caller already knows
      setForm({
        ...emptyForm,
        vehicle_id: prefill?.vehicle?.id || null,
        customer_id: prefill?.customer?.id || null,
        tire_set_type: prefill?.tire_set_type || emptyForm.tire_set_type,
        status: prefill?.status || emptyForm.status,
      });
      setHasRearSize(false);
      setCustomerMode('existing');
      setNewCustomer({ ...EMPTY_NEW_CUSTOMER });
      setNewVehicle({ ...emptyNewVehicle });
      setSelectedCustomer(prefill?.customer || null);
      setSelectedVehicle(prefill?.vehicle || null);
      setSelectedLocation(null);
      setSubmitError(null);
    }
    setIsDirty(false);
    // prefill is read when the dialog opens; a new object on every parent render must not reset it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setIsDirty(true);
  };

  const handleRearSizeToggle = (e) => {
    const checked = e.target.checked;
    setHasRearSize(checked);
    if (!checked) setForm((prev) => ({ ...prev, tire_size_rear: '' }));
    setIsDirty(true);
  };

  const handleDecimalChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleDateChange = (field) => (date) => {
    setForm((prev) => ({ ...prev, [field]: date }));
    setIsDirty(true);
  };

  const formatDateForApi = (date) => {
    if (!date) return null;
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return null;
    }
  };

  const isNewVehicleValid = () => {
    return !!newVehicle.registration_number?.trim();
  };

  const isValid = (() => {
    const customerOk = customerMode === 'existing' ? !!selectedCustomer : isNewCustomerValid(newCustomer);
    const vehicleOk = card ? !!selectedVehicle : (!!selectedVehicle || isNewVehicleValid());
    return customerOk && vehicleOk;
  })();

  const handleSubmit = async () => {
    setSaving(true);
    setSubmitError(null);

    try {
      const headers = {
        'X-CSRFToken': getCsrfToken(),
        'X-Organization-Id': currentOrganization.organization_id,
      };

      let customerId = selectedCustomer?.id || form.customer_id;
      let vehicleId = selectedVehicle?.id || form.vehicle_id;

      // Create new customer if needed
      if (customerMode === 'new' && isNewCustomerValid(newCustomer)) {
        const custResp = await axios.post('/api/customers/', newCustomer, { headers });
        customerId = custResp.data.id;
      }

      // Build payload — vehicle handled as nested object (same as maintenance creation)
      const payload = {
        customer_id: customerId,
        ...(!vehicleId && isNewVehicleValid()
          ? {
              vehicle_data: {
                registration_number: newVehicle.registration_number.trim().toUpperCase(),
                manufacturer: newVehicle.manufacturer || '',
                model: newVehicle.model || '',
                year_model: newVehicle.year_model || null,
                vin: newVehicle.vin || '',
              },
            }
          : { vehicle_id: vehicleId }
        ),
        storage_location_id: selectedLocation?.id || null,
        tire_set_type: form.tire_set_type,
        studded: form.studded,
        rim_type: form.rim_type,
        tire_brand: form.tire_brand,
        tire_model: form.tire_model,
        tire_size: form.tire_size,
        tire_size_rear: hasRearSize ? form.tire_size_rear : '',
        dot_code: form.dot_code,
        tread_depth_fl: form.tread_depth_fl !== '' ? form.tread_depth_fl : null,
        tread_depth_fr: form.tread_depth_fr !== '' ? form.tread_depth_fr : null,
        tread_depth_rl: form.tread_depth_rl !== '' ? form.tread_depth_rl : null,
        tread_depth_rr: form.tread_depth_rr !== '' ? form.tread_depth_rr : null,
        status: form.status,
        storage_start_date: formatDateForApi(form.storage_start_date),
        last_swap_date: formatDateForApi(form.last_swap_date),
        contract_price: form.contract_price !== '' ? form.contract_price : null,
        insurance_included: form.insurance_included,
        service_notes: form.service_notes,
        condition_notes: form.condition_notes,
        notes: form.notes,
      };

      const response = card?.id
        ? await axios.patch(`/api/tire-hotel-cards/${card.id}/`, payload, { headers })
        : await axios.post('/api/tire-hotel-cards/', payload, { headers });

      if (onSaved) onSaved(response.data);
      onClose();
      return true;
    } catch (err) {
      console.error('Error saving hotel card:', err);
      setSubmitError(err.response?.data?.detail || err.response?.data?.error || t('Error'));
      // Return false rather than throw: this is also the button's onClick, where a
      // rejected promise nobody awaits becomes an unhandled rejection.
      return false;
    } finally {
      setSaving(false);
    }
  };

  const isEditMode = !!card;
  const vehicleLocked = !isEditMode && !!prefill?.lockVehicle && !!selectedVehicle;

  // Unsaved changes protection for dialog close (backdrop click / Escape)
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  const { guardedOnClose, UnsavedChangesDialog } = useUnsavedChanges({
    hasChanges: isDirty && open,
    onSave: () => handleSubmitRef.current(),
    mode: 'dialog',
    onClose,
  });

  return (
    <>
    <Dialog open={open} onClose={guardedOnClose} maxWidth="md" fullWidth data-testid="hotel-card-form-dialog">
      <DialogTitle>
        {card ? t('tire_hotel.edit_card') : t('tire_hotel.new_card')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>

          {/* Section: Customer */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('tire_hotel.customer')}
            </Typography>

            {!isEditMode && (
              <ToggleButtonGroup
                value={customerMode}
                exclusive
                onChange={(e, v) => { if (v) { setCustomerMode(v); setSubmitError(null); } }}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton value="existing" sx={{ textTransform: 'none' }}>
                  <PersonSearchIcon sx={{ mr: 1 }} /> {t('tire_hotel.existing_customer')}
                </ToggleButton>
                <ToggleButton value="new" sx={{ textTransform: 'none' }}>
                  <AddIcon sx={{ mr: 1 }} /> {t('tire_hotel.new_customer')}
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {customerMode === 'existing' && (
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => customerLabel(option)}
                loading={customerSearchLoading}
                value={selectedCustomer}
                data-testid="hotel-card-form-customer"
                onChange={(e, value) => {
                  setSelectedCustomer(value);
                  if (!vehicleLocked) {
                    setSelectedVehicle(null);
                    setVehicleSearchInput('');
                  }
                  setForm((prev) => ({ ...prev, customer_id: value?.id || null }));
                }}
                filterOptions={(x) => x}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') setCustomerSearchInput(value);
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {option.type === 'company' ? (
                          <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        ) : (
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        )}
                        <Typography variant="body2" fontWeight={500}>{customerLabel(option)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        {option.email && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <EmailIcon sx={{ fontSize: 12 }} /> {option.email}
                          </Typography>
                        )}
                        {option.phone && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <PhoneIcon sx={{ fontSize: 12 }} /> {option.phone}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    label={t('tire_hotel.select_customer')}
                    required
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            )}

            {customerMode === 'new' && (
              <NewCustomerForm t={t} value={newCustomer} onChange={setNewCustomer} size="small" />
            )}
          </Box>

          {/* Section: Vehicle */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('tire_hotel.vehicle')}
            </Typography>

            {vehicleLocked ? (
              <Box
                data-testid="hotel-card-form-vehicle-locked"
                sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
              >
                <Typography variant="body1" sx={{ ...plateFont, fontWeight: 700 }}>
                  {selectedVehicle.registration_number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {[selectedVehicle.manufacturer, selectedVehicle.model].filter(Boolean).join(' ')}
                </Typography>
              </Box>
            ) : isEditMode ? (
              <Autocomplete
                options={vehicles}
                data-testid="hotel-card-form-vehicle"
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const parts = [option.manufacturer, option.model].filter(Boolean).join(' ');
                  return option.registration_number ? `${option.registration_number} - ${parts}` : parts;
                }}
                loading={vehiclesLoading}
                value={selectedVehicle}
                onChange={(e, value) => {
                  setSelectedVehicle(value);
                  setForm((prev) => ({ ...prev, vehicle_id: value?.id || null }));
                }}
                filterOptions={(x) => x}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') setVehicleSearchInput(value);
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    label={t('tire_hotel.select_vehicle')}
                    required
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {vehiclesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            ) : (
              <VehicleRegistrationField
                dataTestId="hotel-card-form-vehicle"
                vehicle={newVehicle}
                required
                onChange={(next) => {
                  setNewVehicle(next);
                  setIsDirty(true);
                  setSubmitError(null);
                }}
                onExistingVehicle={(existing) => {
                  setSelectedVehicle(existing);
                  setForm((prev) => ({ ...prev, vehicle_id: existing?.id || null }));
                }}
                onLastCustomer={(customer) => {
                  // Same courtesy as work order creation: the vehicle's last customer
                  // is filled in, but never over a customer the user already picked
                  if (customer && customerMode === 'existing' && !selectedCustomer) {
                    setSelectedCustomer(customer);
                  }
                }}
              />
            )}
          </Box>

          {/* Section: Tire Info */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('tire_hotel.tire_info')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t('tire_hotel.tire_set_type')}</InputLabel>
                <Select
                  value={form.tire_set_type}
                  onChange={handleChange('tire_set_type')}
                  label={t('tire_hotel.tire_set_type')}
                  SelectDisplayProps={{ 'data-testid': 'hotel-card-form-season' }}
                >
                  {TIRE_SET_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{t(`tire_hotel.${type}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Checkbox checked={form.studded} onChange={(e) => setForm((prev) => ({ ...prev, studded: e.target.checked }))} />}
                label={t('tire_hotel.studded')}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t('tire_hotel.rim_type')}</InputLabel>
                <Select value={form.rim_type} onChange={handleChange('rim_type')} label={t('tire_hotel.rim_type')}>
                  {RIM_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{t(`tire_hotel.${type}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <TextField label={t('tire_hotel.tire_brand')} value={form.tire_brand} onChange={handleChange('tire_brand')} size="small" sx={{ flex: 1, minWidth: 120 }} data-testid="hotel-card-form-brand" />
              <TextField label={t('tire_hotel.tire_model')} value={form.tire_model} onChange={handleChange('tire_model')} size="small" sx={{ flex: 1, minWidth: 120 }} data-testid="hotel-card-form-model" />
              <TextField label={hasRearSize ? t('tire_hotel.tire_size_front') : t('tire_hotel.tire_size')} value={form.tire_size} onChange={handleChange('tire_size')} size="small" placeholder="205/55R16" sx={{ flex: 1, minWidth: 120 }} data-testid="hotel-card-form-size" />
              {hasRearSize && (
                <TextField label={t('tire_hotel.tire_size_rear')} value={form.tire_size_rear} onChange={handleChange('tire_size_rear')} size="small" placeholder="225/50R16" sx={{ flex: 1, minWidth: 120 }} data-testid="hotel-card-form-size-rear" />
              )}
              <TextField label={t('tire_hotel.dot_code')} value={form.dot_code} onChange={handleChange('dot_code')} size="small" sx={{ flex: 1, minWidth: 100 }} />
            </Box>
            <FormControlLabel
              control={<Checkbox checked={hasRearSize} onChange={handleRearSizeToggle} />}
              label={t('tire_hotel.different_rear_size')}
              data-testid="hotel-card-form-different-rear"
              sx={{ mt: 1 }}
            />
          </Box>

          {/* Section: Tread Depths */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('tire_hotel.tread_depth')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, maxWidth: 400 }}>
              <DecimalField label={t('tire_hotel.tread_depth_fl')} value={form.tread_depth_fl} onChange={handleDecimalChange('tread_depth_fl')} maxDecimals={1} size="small" InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
              <DecimalField label={t('tire_hotel.tread_depth_fr')} value={form.tread_depth_fr} onChange={handleDecimalChange('tread_depth_fr')} maxDecimals={1} size="small" InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
              <DecimalField label={t('tire_hotel.tread_depth_rl')} value={form.tread_depth_rl} onChange={handleDecimalChange('tread_depth_rl')} maxDecimals={1} size="small" InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
              <DecimalField label={t('tire_hotel.tread_depth_rr')} value={form.tread_depth_rr} onChange={handleDecimalChange('tread_depth_rr')} maxDecimals={1} size="small" InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Box>
          </Box>

          {/* Section: Storage & Status. Hidden when the tire swap stores the set itself. */}
          {vehicleLocked ? (
            <Alert severity="info">{t('tire_hotel.swap_new_card_hint')}</Alert>
          ) : (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('tire_hotel.storage_info')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option?.label || ''}
                loading={locationsLoading}
                value={selectedLocation}
                data-testid="hotel-card-form-location"
                onChange={(e, value) => {
                  setSelectedLocation(value);
                  setForm((prev) => ({ ...prev, storage_location_id: value?.id || null }));
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                filterOptions={(x) => x}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') setLocationSearch(value);
                  if (reason === 'clear') setLocationSearch('');
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span>{option.label}</span>
                      {option.fits_size === false && (
                        <Typography variant="caption" color="warning.main" sx={{ ml: 'auto' }}>
                          {t('tire_hotel.size_may_not_fit')}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                noOptionsText={t('tire_hotel.no_locations_found')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    label={t('tire_hotel.select_location')}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{t('tire_hotel.status')}</InputLabel>
                <Select value={form.status} onChange={handleChange('status')} label={t('tire_hotel.status')}>
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{t(`tire_hotel.${s}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                <DatePicker label={t('tire_hotel.storage_start_date')} value={form.storage_start_date} onChange={handleDateChange('storage_start_date')} slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }} />
                <DatePicker label={t('tire_hotel.last_swap_date')} value={form.last_swap_date} onChange={handleDateChange('last_swap_date')} slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }} />
              </LocalizationProvider>
            </Box>
          </Box>
          )}

          {/* Section: Contract & Notes */}
          <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <DecimalField label={t('tire_hotel.contract_price')} value={form.contract_price} onChange={handleDecimalChange('contract_price')} maxDecimals={2} size="small" InputProps={{ endAdornment: <InputAdornment position="end">&euro;</InputAdornment> }} sx={{ maxWidth: 200 }} />
              <FormControlLabel
                control={<Checkbox checked={form.insurance_included} onChange={(e) => setForm((prev) => ({ ...prev, insurance_included: e.target.checked }))} />}
                label={t('tire_hotel.insurance_included')}
              />
            </Box>
            <TextField label={t('tire_hotel.service_notes')} value={form.service_notes} onChange={handleChange('service_notes')} size="small" fullWidth multiline rows={2} sx={{ mb: 2 }} />
            <TextField label={t('tire_hotel.condition_notes')} value={form.condition_notes} onChange={handleChange('condition_notes')} size="small" fullWidth multiline rows={2} sx={{ mb: 2 }} />
            <TextField label={t('tire_hotel.notes')} value={form.notes} onChange={handleChange('notes')} size="small" fullWidth multiline rows={2} />
          </Box>

          {submitError && (
            <Alert severity="error">{submitError}</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !isValid}
          data-testid="hotel-card-form-save"
        >
          {saving ? <CircularProgress size={20} /> : t('Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>
    {UnsavedChangesDialog}
    </>
  );
}

export default TireHotelCardForm;
